// Pure helpers for the Printers part of the Offline mode screen: no React, no bridge, no network.
// The engine's printer control API (GET /_oa/printers, POST /_oa/printers {action}, GET /_oa/print-jobs, POST /_oa/print-retry|print-discard)
// is described in orderart-offline-pos README, "Printing".

export const PAPER_WIDTHS = [
  { cols: 48, label: '80 mm paper (48 columns)' },
  { cols: 42, label: '80 mm paper, large print (42 columns)' },
  { cols: 32, label: '58 mm paper (32 columns)' },
];

const str = (v) => (typeof v === 'string' && v ? v : '');
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** GET /_oa/printers -> { capable, assumedRoles, printers[], settings } with every field clamped to a safe shape (never throws). */
export function normalizePrinters(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const printers = (Array.isArray(r.printers) ? r.printers : [])
    .filter((p) => p && Number.isFinite(Number(p.id)))
    .map((p) => {
      const roles = p.roles && typeof p.roles === 'object' ? p.roles : {};
      return {
        id: Number(p.id),
        label: str(p.label) || `Printer ${p.id}`,
        topicTail: str(p.topic_tail),
        active: p.active !== false,
        primary: p.primary === true,
        ip: str(p.ip),
        port: num(p.port) || 9100,
        bound: p.bound === true,
        identity: str(p.identity),
        reachable: typeof p.reachable === 'boolean' ? p.reachable : null,
        problem: str(p.problem),
        cols: num(p.cols) || 48,
        rolesSource: str(p.roles_source) || 'none',
        roles: {
          kitchenTakeaway: num(roles.kitchen_takeaway),
          kitchenDineIn: num(roles.kitchen_dine_in),
          billingTakeaway: num(roles.billing_takeaway),
          billingDineIn: num(roles.billing_dine_in),
          onDemand: num(roles.on_demand),
          drawer: roles.drawer === true,
        },
      };
    });
  const s = r.settings && typeof r.settings === 'object' ? r.settings : {};
  return {
    capable: r.capable === true,
    assumedRoles: r.assumedRoles === true,
    printers,
    settings: { restaurantName: str(s.restaurant_name), abn: str(s.abn), phone: str(s.phone), multiStation: str(s.multi_station) || 'auto' },
  };
}

const copies = (n) => (n > 1 ? ` x${n}` : '');

/** What the printer is for, in words: "Kitchen x2, Receipt, Cash drawer". "Not used" when it holds no role. */
export function rolesText(p) {
  const r = p?.roles || {};
  const parts = [];
  const kitchen = Math.max(r.kitchenTakeaway || 0, r.kitchenDineIn || 0);
  const billing = Math.max(r.billingTakeaway || 0, r.billingDineIn || 0);
  if (kitchen > 0) parts.push(`Kitchen${copies(kitchen)}`);
  if (billing > 0) parts.push(`Receipt${copies(billing)}`);
  if (r.onDemand > 0) parts.push('Bills on request');
  if (r.drawer) parts.push('Cash drawer');
  return parts.length ? parts.join(', ') : 'Not used for any station';
}

/** Where the roles come from. */
export function rolesSourceText(source) {
  if (source === 'manager') return 'Set on this device';
  if (source === 'assumed') return 'Assumed (no station is set in the restaurant settings)';
  if (source === 'cloud') return 'From the restaurant settings';
  return '';
}

const capital = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** { label, tone: 'ok'|'warn'|'bad', detail } for one printer. */
export function printerStatus(p) {
  if (!p) return { label: '', tone: 'warn', detail: '' };
  if (!p.active) return { label: 'Switched off', tone: 'warn', detail: 'This printer is switched off in the restaurant settings.' };
  if (!p.bound || !p.ip) return { label: 'No address', tone: 'bad', detail: 'Find printers on the network, or enter its IP address.' };
  if (p.reachable === false) return { label: 'Not reachable', tone: 'bad', detail: p.problem ? capital(p.problem) : 'It did not answer. Check it is on and on the same Wi-Fi.' };
  if (p.reachable === true && p.problem) return { label: capital(p.problem), tone: 'bad', detail: 'The printer reports a problem. Fix it and the waiting dockets print.' };
  if (p.reachable === true) return { label: 'Ready', tone: 'ok', detail: '' };
  return { label: 'Not checked yet', tone: 'warn', detail: 'It is checked about once a minute while offline. Use "Check now" or "Test print".' };
}

/** A dotted-quad IPv4 address: four numbers 0-255. Returns the normalised address or null. */
export function parseIPv4(text) {
  const m = /^\s*(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\s*$/.exec(String(text || ''));
  if (!m) return null;
  const parts = m.slice(1).map(Number);
  return parts.every((n) => n >= 0 && n <= 255) ? parts.join('.') : null;
}

/** { ok, ip, port, error } for the manual "Enter IP address" form. The port is optional (default 9100). */
export function validateAddress(ipText, portText) {
  const ip = parseIPv4(ipText);
  if (!ip) return { ok: false, error: 'Enter the printer address as four numbers, like 192.168.1.50.' };
  if (ip === '0.0.0.0' || ip.startsWith('127.') || ip.startsWith('255.')) return { ok: false, error: 'That address cannot be a printer on the network.' };
  const portStr = String(portText ?? '').trim();
  let port = 9100;
  if (portStr) {
    port = /^\d{1,5}$/.test(portStr) ? Number(portStr) : 0;
    if (port < 1 || port > 65535) return { ok: false, error: 'The port must be a number from 1 to 65535 (printers use 9100).' };
  }
  return { ok: true, ip, port, error: '' };
}

/** The roles body that turns the cash drawer on or off while keeping the other stations as they are (the engine replaces all three flags at once). */
export function drawerRolesBody(p, drawer) {
  const r = p.roles;
  const kitchen = Math.max(r.kitchenTakeaway, r.kitchenDineIn);
  const billing = Math.max(r.billingTakeaway, r.billingDineIn);
  return { action: 'roles', printer_id: p.id, kitchen: kitchen > 0, billing: billing > 0, drawer: Boolean(drawer), kitchen_copies: Math.max(1, kitchen), billing_copies: Math.max(1, billing) };
}

/** discover answer -> { bound: [{printerId,label,ip}], unmatched: [{ip,port,identity,via}] }. */
export function normalizeDiscovery(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    message: str(r.message),
    bound: (Array.isArray(r.bound) ? r.bound : []).map((b) => ({ printerId: num(b.printer_id), label: str(b.label), ip: str(b.ip) })),
    unmatched: (Array.isArray(r.unmatched) ? r.unmatched : []).filter((u) => str(u.ip)).map((u) => ({ ip: str(u.ip), port: num(u.port) || 9100, identity: str(u.identity), via: str(u.via) })),
  };
}

const DOCKET = { kitchen: 'Kitchen docket', billing: 'Receipt', receipt: 'Receipt', bill: 'Bill', callaway: 'Call away', call_away: 'Call away', test: 'Test slip', drawer: 'Cash drawer', reprint: 'Reprint' };
const STATE = { pending: 'Waiting to print', printing: 'Printing', printed: 'Printed', failed: 'Failed', parked: 'Needs a manager', discarded: 'Discarded' };

/** One print job -> { title, detail, state, canAct }. */
export function jobLine(j) {
  const type = DOCKET[String(j?.docket_type || '').toLowerCase()] || (j?.docket_type ? capital(String(j.docket_type)) : 'Print job');
  const title = [type, j?.order_no, j?.printer].filter(Boolean).join(' · ');
  const state = STATE[j?.state] || String(j?.state || '');
  const detail = [state, j?.error ? capital(String(j.error)) : ''].filter(Boolean).join(': ');
  return { title, detail, state: j?.state || '', canAct: ['failed', 'parked', 'pending'].includes(j?.state) };
}

/** The plain-language line about printing for the current mode and the printers' reachability. */
export function printingExplain({ offline, capable, printerCount }) {
  const base = 'Printing from this terminal works only while it is offline. While online, orders print through the cloud as usual.';
  if (!printerCount) return `${base} The restaurant has no printers set up in the cloud.`;
  if (capable) return `${base} At least one printer answered recently, so offline printing is ready.`;
  return `${base} No printer has an address that answered in the last 3 minutes, so offline orders would not print yet.`;
}
